import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Reusable feature/benefit card with cyberpunk accent bar and index number.
 * Used on HomePage, DJsPage, and anywhere feature grids are needed.
 *
 * Props:
 *   icon      - React node (MUI icon)
 *   title     - string
 *   description - string
 *   index     - number (0-based, displayed as 01, 02, etc.)
 */
const FeatureCard = ({ icon, title, description, index }) => (
  <Box
    sx={{
      p: 4,
      bgcolor: 'background.paper',
      border: '2px solid',
      borderColor: 'secondary.light',
      position: 'relative',
      overflow: 'hidden',
      height: '100%',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: 'primary.main',
        '& .feature-number': {
          color: 'primary.main',
        },
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '4px',
        height: '100%',
        bgcolor: 'primary.main',
      },
    }}
  >
    <Typography
      className="feature-number"
      sx={{
        fontFamily: '"Bebas Neue", sans-serif',
        fontSize: '4rem',
        color: 'secondary.light',
        position: 'absolute',
        top: -10,
        right: 16,
        lineHeight: 1,
        transition: 'color 0.2s ease',
      }}
    >
      0{index + 1}
    </Typography>
    <Box
      sx={{
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3,
        '& svg': {
          fontSize: 32,
          color: 'primary.main',
        },
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="h5"
      sx={{
        mb: 2,
        fontFamily: '"Bebas Neue", sans-serif',
        letterSpacing: '0.05em',
        fontSize: '1.5rem',
        color: 'text.primary',
      }}
    >
      {title}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
        lineHeight: 1.8,
      }}
    >
      {description}
    </Typography>
  </Box>
);

export default FeatureCard;
